import type {
  ForkBlock,
  PipelineState,
  ScriptBlock,
  StageStatus,
  SyncStatus,
  UnpausedForkStatus,
} from "@gearbox-protocol/deploy-tools-shared";
import {
  Button,
  CopyToClipboard,
  HelpTooltip,
  NetworkLogo,
  NewTab,
} from "@gearbox-protocol/deploy-tools-ui";
import { ADDRESS_0X0, etherscanUrl } from "@gearbox-protocol/sdk/onchain";
import GrafanaLink from "components/GrafanaLink";
import StatusDot from "components/StatusDot";
import { format, formatDistanceToNow } from "date-fns";
import { useCopyToast } from "hooks/useCopyToast";
import type { FC } from "react";
import { NavLink } from "react-router-dom";
import { buildSdkAttachSnippet } from "utils";

import BackendBlock from "./BackendBlock";
import BlockDrift from "./BlockDrift";
import { Script } from "./Script";

function getDeployBlock({
  scripts,
}: UnpausedForkStatus): ScriptBlock | undefined {
  const blocks =
    scripts?.map(s => s.blockAfter).filter((b): b is ScriptBlock => !!b) ?? [];
  if (blocks.length === 0) {
    return undefined;
  }
  return blocks.sort((a, b) => a.number - b.number).pop();
}

const STATE_DOTS: Record<PipelineState, StageStatus> = {
  queued: "queued",
  deploying: "started",
  deployed: "success",
  failed: "failed",
};

function getSyncDot(sync?: SyncStatus): StageStatus {
  switch (sync?.state) {
    case "ok":
      return "success";
    case "failing":
      return "error";
    default:
      return "queued";
  }
}

function getBlockDelta(fork?: ForkBlock, deploy?: ForkBlock): string {
  const delta = deploy && fork ? deploy.number - fork.number : 0;
  return delta > 0 ? ` (+${delta})` : "";
}

export interface UnpausedForkStatusRouteProps {
  fork: UnpausedForkStatus;
}

export const UnpausedForkStatusRoute: FC<UnpausedForkStatusRouteProps> = ({
  fork,
}) => {
  const {
    rpcUrl,
    alias,
    executionId,
    state,
    error,
    addressProvider,
    marketConfigurators,
    rwaFactories,
    originChainId,
    anvilChainId,
    forkBlock,
    sync,
    latestBlock,
    createdAt,
    nextRestart,
    scripts,
    options,
  } = fork;

  const copy = useCopyToast();
  const deployBlock = getDeployBlock(fork);
  const delta = getBlockDelta(forkBlock, deployBlock);
  const createdStr = createdAt
    ? formatDistanceToNow(new Date(createdAt), { addSuffix: true })
    : "-";
  const nextRestartStr = nextRestart
    ? formatDistanceToNow(new Date(nextRestart), { addSuffix: true })
    : "-";

  return (
    <div className="flex flex-col flex-1 rounded-xl bg-background p-4">
      <table className="table-auto border-collapse border border-slate-500">
        <tbody>
          <tr>
            <td className="border border-slate-500 p-2 min-w-40">
              Execution ID
            </td>
            <td className="border border-slate-500 p-2">
              <NavLink to={`/forks/${executionId}`}>{executionId}</NavLink>
              <span>
                <CopyToClipboard text={executionId} />
              </span>
              {!!alias && (
                <>
                  <span className="border-slate-500 border-l ml-2 pl-2">
                    {alias}
                  </span>
                  <span>
                    <CopyToClipboard text={alias} />
                  </span>
                </>
              )}
            </td>
          </tr>

          <tr>
            <td className="border border-slate-500 p-2">State</td>
            <td className="border border-slate-500 p-2">
              <StatusDot status={STATE_DOTS[state]} className="inline-block" />
              <span className="ml-1">{state}</span>
              {!!error && (
                <span className="border-slate-500 border-l ml-2 pl-2 text-red-600">
                  <span className="inline-block max-w-md truncate align-bottom">
                    {error}
                  </span>
                  <HelpTooltip className="text-slate-500 -mt-1">
                    {error}
                  </HelpTooltip>
                </span>
              )}
            </td>
          </tr>

          <tr>
            <td className="border border-slate-500 p-2">Original chain ID</td>
            <td className="border border-slate-500 p-2">
              <span>{originChainId}</span>
              <NetworkLogo chainId={originChainId} />
            </td>
          </tr>

          <tr>
            <td className="border border-slate-500 p-2">Chain ID</td>
            <td className="border border-slate-500 p-2">{anvilChainId}</td>
          </tr>

          <tr>
            <td className="border border-slate-500 p-2">RPC Endpoint</td>
            <td className="border border-slate-500 p-2">
              <span>{rpcUrl}</span>
              <span>
                <CopyToClipboard text={rpcUrl} />
              </span>
              <Button
                variant="link"
                className="h-auto p-0 ml-2 text-slate-400"
                onClick={() =>
                  copy(buildSdkAttachSnippet(fork), "SDK snippet copied")
                }
              >
                Copy SDK snippet
              </Button>
            </td>
          </tr>

          <tr>
            <td className="border border-slate-500 p-2">Logs</td>
            <td className="border border-slate-500 p-2 ">
              <div className="flex flex-row items-center gap-2 divide-x *:px-1 -mx-1 divide-slate-500">
                <GrafanaLink executionId={executionId}>Grafana</GrafanaLink>

                <GrafanaLink
                  executionId={executionId}
                  labels={{ service_name: "optimist", runner: "true" }}
                  json
                >
                  Optimist
                </GrafanaLink>

                <GrafanaLink
                  executionId={executionId}
                  labels={{ role: "synctron" }}
                  json
                >
                  Synctron
                </GrafanaLink>
              </div>
            </td>
          </tr>

          <tr>
            <td className="border border-slate-500 p-2">Created at</td>
            <td className="border border-slate-500 p-2">
              <span>{createdStr}</span>
              {!!createdAt && (
                <span className="text-slate-500">{` (${format(new Date(createdAt), "HH:mm:ss dd/MM/yyyy")})`}</span>
              )}
            </td>
          </tr>

          <tr>
            <td className="border border-slate-500 p-2">Next restart</td>
            <td className="border border-slate-500 p-2">{nextRestartStr}</td>
          </tr>

          <tr>
            <td className="border border-slate-500 p-2 align-middle">
              Fork block
              <HelpTooltip className="text-slate-500">
                block number passed as anvil fork block argument
              </HelpTooltip>
            </td>
            <td className="border border-slate-500 p-2">
              {forkBlock ? (
                <a
                  href={etherscanUrl(
                    { block: forkBlock.number },
                    originChainId,
                  )}
                  target="_blank"
                  rel="noopener"
                >
                  <span>{forkBlock.number}</span>
                  <span className="text-slate-500">{` (${format(new Date(forkBlock.time), "HH:mm:ss dd/MM/yyyy")})`}</span>
                  {!!options.blockNumber && (
                    <span className="ml-1 pl-1 font-mono text-sm text-slate-500 border-l-slate-500 border-l">
                      {options.blockNumber}
                    </span>
                  )}
                  <NewTab className="text-slate-500" />
                </a>
              ) : (
                "-"
              )}
            </td>
          </tr>

          <tr>
            <td className="border border-slate-500 p-2 align-middle">
              Deploy block
              <HelpTooltip className="text-slate-500">
                block number after latest script finished running
              </HelpTooltip>
            </td>
            <td className="border border-slate-500 p-2">
              {deployBlock ? (
                <>
                  <span>{deployBlock.number}</span>
                  <span>{`${delta}`}</span>
                  <span className="text-slate-500">
                    {` (${format(new Date(deployBlock.time), "HH:mm:ss dd/MM/yyyy")})`}
                  </span>
                </>
              ) : (
                "-"
              )}
            </td>
          </tr>

          <tr>
            <td className="border border-slate-500 p-2 align-middle">
              Sync
              <HelpTooltip className="text-slate-500">
                price feed sync with the origin chain
              </HelpTooltip>
            </td>
            <td className="border border-slate-500 p-2">
              <StatusDot status={getSyncDot(sync)} className="inline-block" />
              <span className="ml-1">{sync?.state ?? "-"}</span>
              {!!sync?.lastSyncedAt && (
                <span className="border-slate-500 border-l ml-2 pl-2 text-slate-500">
                  {`last synced ${formatDistanceToNow(new Date(sync.lastSyncedAt), { addSuffix: true })}`}
                </span>
              )}
              {!!sync?.error && (
                <span className="border-slate-500 border-l ml-2 pl-2 text-red-600">
                  <span>⚠️ Sync error: </span>
                  <span className="inline-block max-w-md truncate align-bottom">
                    {sync.error}
                  </span>
                  <HelpTooltip className="text-slate-500 -mt-1">
                    {sync.error}
                  </HelpTooltip>
                </span>
              )}
            </td>
          </tr>

          <tr>
            <td className="border border-slate-500 p-2 align-middle">
              Latest block
              <HelpTooltip className="text-slate-500">
                current block number as reported by backend
              </HelpTooltip>
            </td>
            <td className="border border-slate-500 p-2">
              {latestBlock ? (
                <>
                  <span>{latestBlock.number}</span>
                  <span className="text-slate-500">{` (${format(new Date(latestBlock.time), "HH:mm:ss dd/MM/yyyy")})`}</span>
                  <BlockDrift block={latestBlock} warningRange={[-60, 12]} />
                </>
              ) : (
                "-"
              )}
            </td>
          </tr>

          {fork.isActive && (
            <tr>
              <td className="border border-slate-500 p-2 align-middle">
                Backend
                <HelpTooltip className="text-slate-500">
                  backend sidecar latest block and status
                </HelpTooltip>
              </td>
              <td className="border border-slate-500 p-2">
                <BackendBlock
                  executionId={executionId}
                  chainId={anvilChainId}
                  backend={fork.backend}
                  latestBlock={latestBlock}
                />
              </td>
            </tr>
          )}

          <tr>
            <td className="border border-slate-500 p-2">Address provider</td>
            <td className="border border-slate-500 p-2">
              {addressProvider === ADDRESS_0X0 ? (
                "-"
              ) : (
                <>
                  <span>{addressProvider}</span>
                  <span>
                    <CopyToClipboard text={addressProvider} />
                  </span>
                </>
              )}
            </td>
          </tr>

          {!!marketConfigurators && (
            <tr>
              <td className="border border-slate-500 p-2">
                Market configurators
              </td>
              <td className="border border-slate-500 p-2">
                <div className="flex flex-row items-center gap-2 flex-wrap">
                  {marketConfigurators.map(mc => (
                    <div key={mc} className="flex flex-row items-center gap-1">
                      <span>{mc}</span>
                      <CopyToClipboard text={mc} />
                    </div>
                  ))}
                </div>
              </td>
            </tr>
          )}

          {!!rwaFactories && (
            <tr>
              <td className="border border-slate-500 p-2">RWA factories</td>
              <td className="border border-slate-500 p-2">
                <div className="flex flex-row items-center gap-2 flex-wrap">
                  {rwaFactories.map(addr => (
                    <div
                      key={addr}
                      className="flex flex-row items-center gap-1"
                    >
                      <span>{addr}</span>
                      <CopyToClipboard text={addr} />
                    </div>
                  ))}
                </div>
              </td>
            </tr>
          )}

          {!!scripts?.length && (
            <tr>
              <td className="border border-slate-500 p-2 align-top">
                Scripts:
              </td>
              <td className="border border-slate-500 p-2">
                <ul>
                  {scripts.map(s => (
                    <Script
                      key={s.script.id}
                      script={s}
                      executionId={executionId}
                    />
                  ))}
                </ul>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
